import { ListSection } from '@/components/recycler-list';
import { normalizeQuotes } from '@/utils/string';
import { useCallback, useMemo, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

export const SEARCH_FILTER_DEBOUNCE_DELAY_MS = 250;

type FilterArgs<T> =
  | {
      data: T[];
      getSearchableFields: (item: T) => string[];
      removeEmptySections?: never;
      sectionList?: false;
    }
  | {
      data: ListSection<T>[];
      getSearchableFields: (item: T) => string[];
      removeEmptySections?: boolean;
      sectionList: true;
    };

export const useMultiSearchFilters = <T extends {}>({
  data,
  getSearchableFields,
  removeEmptySections = true,
  sectionList,
}: FilterArgs<T>) => {
  const [searchFilters, setSearchFilters] = useState<string[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchText, setSearchText] = useState('');

  const onSearchInputChangeText = useDebouncedCallback((text) => {
    setSearchFilters(
      normalizeQuotes(text).toLowerCase().split(/\s+/).filter(Boolean),
    );
    setSearching(false);
  }, SEARCH_FILTER_DEBOUNCE_DELAY_MS);

  const sectionData = useMemo<ListSection<T>[]>(() => {
    if (!sectionList) {
      return [];
    } else if (searchFilters.length === 0) {
      return data;
    }

    return data.reduce<ListSection<T>[]>((acc, section) => {
      const matchingData = section.data.filter((item) => {
        const searchableText = normalizeQuotes(
          getSearchableFields(item)
            .map((field) => field.toLowerCase().trim())
            .join(' '),
        );

        return searchFilters.every((filter) => searchableText.includes(filter));
      });

      if (matchingData.length > 0 || !removeEmptySections) {
        acc.push({
          ...section,
          data: matchingData,
        });
      }
      return acc;
    }, []);
  }, [
    data,
    getSearchableFields,
    removeEmptySections,
    searchFilters,
    sectionList,
  ]);

  const filteredData = useMemo<T[]>(() => {
    if (sectionList) {
      return [];
    } else if (searchFilters.length === 0) {
      return data;
    }
    return data.filter((item) => {
      const searchableText = normalizeQuotes(
        getSearchableFields(item)
          .map((field) => field.toLowerCase().trim())
          .join(' '),
      );

      return searchFilters.every((filter) => searchableText.includes(filter));
    });
  }, [data, getSearchableFields, searchFilters, sectionList]);

  const clearSearch = useCallback(() => {
    onSearchInputChangeText.cancel();
    setSearchFilters([]);
    setSearchText('');
    setSearching(false);
  }, [onSearchInputChangeText]);

  const onDisplayTextChange = useCallback(
    (text: string) => {
      setSearching(true);
      onSearchInputChangeText(text);
      setSearchText(text);
    },
    [onSearchInputChangeText],
  );

  return useMemo(() => {
    return {
      clearSearch,
      filteredData,
      onDisplayTextChange,
      searching,
      searchText,
      sectionData,
    };
  }, [
    clearSearch,
    filteredData,
    onDisplayTextChange,
    searchText,
    searching,
    sectionData,
  ]);
};
